import { createSlice } from '@reduxjs/toolkit';
import {
   CALL_MODES,
   CALL_TYPE,
   CALLKIT_CALLSCREENSTATUS,
   CALLKIT_CALLUISTATE,
   CALLKIT_MAXTILES,
   CALLKIT_VIEWMODE,
} from '../constants';

const initialState = {
   participants: [],
   pinnedId: null,
   activeSpeakerId: null,
   viewMode: CALLKIT_VIEWMODE.SIDEBAR, // 'auto' | 'tiled' | 'spotlight' | 'sidebar'
   maxTiles: CALLKIT_MAXTILES,
   hideNoVideo: false,
   callScreenStatus: CALLKIT_CALLSCREENSTATUS.IDLE, // 'idle' | 'incoming' | 'outgoing' | 'connected' | 'ended'
   callUIState: CALLKIT_CALLUISTATE.MAXIMIZED, // 'hidden' | 'maximized' | 'minimized'
   makeCallDetails: null,
   incomingCallDetails: null,
   callMode: CALL_MODES.ONETOONE,
   callType: CALL_TYPE.AUDIO,
   ongoingCallParticipants: [],
   visibleParticipants: [],
   hiddenParticipants: [],
   callSwitchState: { switchAction: null, fromUser: null },
   groupId: null,
   groupName: null,
   callLink: null,
   callConnectionTime: null,
   showParticipantsPanel: false,
   missedCallCount: 0,
   refreshCallLogFlag: false,
   activeScreenShares: [],
};

const syncVisibility = state => {
   const all = state.ongoingCallParticipants || [];
   const max = state.maxTiles || 8;
   if (all.length > max) {
      state.visibleParticipants = all.slice(0, max - 1);
      state.hiddenParticipants = all.slice(max - 1);
   } else {
      state.visibleParticipants = all;
      state.hiddenParticipants = [];
   }
};

const callkitDataSlice = createSlice({
   name: 'callkitData',
   initialState,
   reducers: {
      setMissedCallCount: (state, action) => {
         state.missedCallCount = action.payload;
      },
      setCallScreenStatus: (state, action) => {
         state.callScreenStatus = action.payload;
         if (action.payload === CALLKIT_CALLSCREENSTATUS.CONNECTED && !state.callConnectionTime) {
            state.callConnectionTime = Date.now();
         } else if (action.payload === CALLKIT_CALLSCREENSTATUS.IDLE) {
            state.callConnectionTime = null;
         }
      },
      setMakeCallDetails: (state, action) => {
         state.makeCallDetails = action.payload;
         state.callScreenStatus = CALLKIT_CALLSCREENSTATUS.OUTGOING;
         state.callUIState = CALLKIT_CALLUISTATE.MAXIMIZED;
         state.callMode = action.payload.callMode;
         state.callType = action.payload.callType;
         state.groupId = action.payload.groupId;
         state.groupName = action.payload.groupName;
         state.viewMode =
            action.payload.callMode === CALL_MODES.ONETOMANY || action.payload.callMode === CALL_MODES.MEET
               ? CALLKIT_VIEWMODE.TILED
               : CALLKIT_VIEWMODE.SIDEBAR;
      },
      setIncomingCallDetails: (state, action) => {
         state.incomingCallDetails = action.payload;
         state.callScreenStatus = CALLKIT_CALLSCREENSTATUS.INCOMING;
         state.callUIState = CALLKIT_CALLUISTATE.MAXIMIZED;
         state.callMode = action.payload.callMode;
         state.callType = action.payload.callType;
         state.groupId = action.payload.callMode == CALL_MODES.ONETOMANY ? action.payload.chatId : null;
         state.groupName = action.payload.chatName || action.payload.groupName;
         state.callLink = action.payload.joinUri;
         state.viewMode =
            action.payload.callMode === CALL_MODES.ONETOMANY || action.payload.callMode === CALL_MODES.MEET
               ? CALLKIT_VIEWMODE.TILED
               : CALLKIT_VIEWMODE.SIDEBAR;
      },
      setCallUIState: (state, action) => {
         state.callUIState = action.payload;
      },
      addParticipant: (state, action) => {
         const exists = state.participants.find(p => p.id === action.payload.id);
         if (!exists) {
            state.participants.push(action.payload);
         }
      },
      removeParticipant: (state, action) => {
         state.participants = state.participants.filter(p => p.id !== action.payload);
      },
      updateParticipant: (state, action) => {
         const { id, changes } = action.payload;
         const index = state.participants.findIndex(p => p.id === id);
         if (index !== -1) {
            state.participants[index] = { ...state.participants[index], ...changes };
         }
      },
      toggleMute: (state, action) => {
         const id = action.payload;
         const p = state.participants.find(p => p.id === id);
         if (p) p.muted = !p.muted;
      },
      toggleVideo: (state, action) => {
         const id = action.payload;
         const p = state.participants.find(p => p.id === id);
         if (p) p.videoOn = !p.videoOn;
      },
      setPinnedId: (state, action) => {
         if (state.pinnedId === action.payload) {
            state.pinnedId = null;
         } else if (action.payload) {
            state.pinnedId = action.payload;
            state.viewMode = CALLKIT_VIEWMODE.SIDEBAR;
         } else {
            state.pinnedId = null;
         }
      },
      setActiveSpeakerId: (state, action) => {
         state.activeSpeakerId = action.payload;
      },
      setViewMode: (state, action) => {
         state.viewMode = action.payload;
         if (action.payload === CALLKIT_VIEWMODE.TILED) {
            state.pinnedId = null;
         }
      },
      setMaxTiles: (state, action) => {
         state.maxTiles = action.payload;
         syncVisibility(state);
      },
      setHideNoVideo: (state, action) => {
         state.hideNoVideo = action.payload;
      },
      resetCallkitData: state => {
         return { ...initialState, refreshCallLogFlag: state.refreshCallLogFlag, activeScreenShares: [] };
      },
      initializeParticipants: (state, action) => {
         // Dedup by normalized userId so a caller listed both in the calleeId
         // list and as the explicit local-user push (or any duplicate from the
         // SDK's participants payload) doesn't render twice on the call screen.
         const seen = new Set();
         const unique = [];
         for (const p of action.payload || []) {
            const id = p?.userId;
            if (!id) continue;
            const key = String(id).split('@')[0];
            if (seen.has(key)) continue;
            seen.add(key);
            unique.push(p);
         }
         state.ongoingCallParticipants = unique;
         syncVisibility(state);
      },
      updateParticipantStatus: (state, action) => {
         const { userId, ...changes } = action.payload;
         const index = state.ongoingCallParticipants.findIndex(p => p.userId && userId && (p.userId === userId || p.userId.split('@')[0] === userId.split('@')[0]));
         if (index !== -1) {
            state.ongoingCallParticipants[index] = { ...state.ongoingCallParticipants[index], ...changes };
            syncVisibility(state);
         }
      },
      removeParticipantFromCall: (state, action) => {
         const userId = action.payload;
         state.ongoingCallParticipants = state.ongoingCallParticipants.filter(p => !(p.userId && userId && (p.userId === userId || p.userId.split('@')[0] === userId.split('@')[0])));
         if (state.pinnedId && userId && (state.pinnedId === userId || state.pinnedId.split('@')[0] === userId.split('@')[0])) {
            state.pinnedId = null;
         }
         syncVisibility(state);
      },
      updateCallSwitch: (state, action) => {
         state.callSwitchState = { ...state.callSwitchState, ...action.payload };
      },
      setCallType: (state, action) => {
         state.callType = action.payload;
      },
      setCallMode: (state, action) => {
         state.callMode = action.payload;
         state.viewMode =
            action.payload === CALL_MODES.ONETOMANY || action.payload === CALL_MODES.MEET
               ? CALLKIT_VIEWMODE.TILED
               : CALLKIT_VIEWMODE.SIDEBAR;
      },
      addParticipantToCall: (state, action) => {
         const userId = action.payload.userId;
         const exists = state.ongoingCallParticipants.find(p => p.userId && userId && (p.userId === userId || p.userId.split('@')[0] === userId.split('@')[0]));
         if (!exists) {
            state.ongoingCallParticipants = [...state.ongoingCallParticipants, action.payload];
            // Auto-promote 1-1 → group when a 3rd real participant joins
            // (covers remote-initiated Add-Participants where no explicit
            // CALL_MODE_UPDATE arrives from the SDK).
            if (state.callMode === CALL_MODES.ONETOONE) {
               const realParticipants = state.ongoingCallParticipants.filter(
                  p => p.userId && !String(p.userId).endsWith('-screen'),
               );
               if (realParticipants.length > 2) {
                  state.callMode = CALL_MODES.ONETOMANY;
                  state.viewMode = CALLKIT_VIEWMODE.TILED;
               }
            }
            syncVisibility(state);
         }
      },
      swapSpeakerIntoVisible: (state, action) => {
         const { userId, localUser } = action.payload;
         const participants = [...state.ongoingCallParticipants];
         const index = participants.findIndex(p => p.userId === userId);
         const maxVisibleCount = state.maxTiles - 1;
         if (index >= maxVisibleCount) {
            let swapIndex = -1;
            for (let i = 0; i < maxVisibleCount; i++) {
               const p = participants[i];
               if (p.userId !== localUser && !p.userId.endsWith('-screen') && p.callStatus !== 'CONNECTED') {
                  swapIndex = i;
                  break;
               }
            }
            if (swapIndex === -1) {
               for (let i = maxVisibleCount - 1; i >= 0; i--) {
                  const p = participants[i];
                  if (p.userId !== localUser && !p.userId.endsWith('-screen')) {
                     swapIndex = i;
                     break;
                  }
               }
            }
            if (swapIndex !== -1) {
               const temp = participants[swapIndex];
               participants[swapIndex] = participants[index];
               participants[index] = temp;
               state.ongoingCallParticipants = participants;
               syncVisibility(state);
            }
         }
      },
      setCallLink: (state, action) => {
         state.callLink = action.payload;
      },
      setShowParticipantsPanel: (state, action) => {
         state.showParticipantsPanel = action.payload;
      },
      triggerCallLogRefresh: state => {
         state.refreshCallLogFlag = !state.refreshCallLogFlag;
      },
      addScreenShare: (state, action) => {
         if (!state.activeScreenShares.includes(action.payload)) {
            state.activeScreenShares.push(action.payload);
         }
      },
      removeScreenShare: (state, action) => {
         state.activeScreenShares = state.activeScreenShares.filter(id => id !== action.payload);
      },
   },
});

export const {
   setCallScreenStatus,
   setCallUIState,
   addParticipant,
   removeParticipant,
   updateParticipant,
   toggleMute,
   toggleVideo,
   setPinnedId,
   setActiveSpeakerId,
   setViewMode,
   setMaxTiles,
   setHideNoVideo,
   setMakeCallDetails,
   resetCallkitData,
   setIncomingCallDetails,
   initializeParticipants,
   updateParticipantStatus,
   removeParticipantFromCall,
   updateCallSwitch,
   setCallType,
   setCallMode,
   setCallLink,
   addParticipantToCall,
   setShowParticipantsPanel,
   setMissedCallCount,
   triggerCallLogRefresh,
   addScreenShare,
   removeScreenShare,
   swapSpeakerIntoVisible,
} = callkitDataSlice.actions;

export const { reducer: callkitDataReducer } = callkitDataSlice;
